// src/pages/Reports/components/EditReport.tsx
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import BackIcon from "@/styles/all-images/icons/back-button.svg";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { S3Image } from "@/utils/s3-helper";
import ArrowDownIcon from "@/styles/all-images/icons/chevron-down-grey.svg";
import { useDispatch } from "react-redux";
import actionsHandler from "@/store/actions/actionHandler";

interface Company {
  id: number;
  name: string;
}

interface CompanyTier {
  id: number;
  user_type: string;
  companies: Company[];
}

const EditReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expandedSection, setExpandedSection] = useState("");
  const [companies, setCompanies] = useState<CompanyTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<any>(null);
  const { id } = useParams();

  // Add custom URL validation function
  const isValidIframeTag = (value: string) => {
    const urlRegex = /<iframe.*src="(https?:\/\/[^"]+)".*><\/iframe>/;
    return urlRegex.test(value);
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      reportType: "1",
      iframeTag: "",
      companies: [] as string[],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      title: Yup.string()
        .max(100, "Report Title Cannot exceed 100 characters.")
        .required("Report title is required"),
      iframeTag: Yup.string().test(
        "is-valid-iframe",
        "Invalid Iframe Tag. Please enter a valid URL",
        (value) => {
          if (!value) return true;
          return isValidIframeTag(value);
        },
      ),
      companies: Yup.array().min(
        1,
        "Select at least one company before submitting",
      ),
      reportType: Yup.string().oneOf(["1", "2", "3"]).required(),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const { authenticatedCall } = actionsHandler("updateReport");
        const response = await dispatch(
          authenticatedCall({
            method: "put",
            customParams: [id!],
            payload: {
              title: values.title,
              report_type: values.reportType,
              iframe_tag: values.iframeTag,
              companies: values.companies,
            },
          }) as any,
        );

        if (response.ok) {
          toast.success("Report updated successfully");
          navigate(-1);
        } else {
          toast.error(response.error || "Failed to update report");
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const { authenticatedCall } = actionsHandler("tierCompanyList");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
          }) as any,
        );

        if (response.ok && response.data) {
          setCompanies(response.data);
        } else {
          toast.error("Failed to load companies");
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Failed to load companies");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [dispatch]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const { authenticatedCall } = actionsHandler("viewReport");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [id!],
          }) as any,
        );

        if (response.ok && response.data) {
          setReport(response.data);
          const companyIds =
            response.data.companies
              ?.map(
                (company: any) => company.id?.toString() || company?.toString(),
              )
              .filter(Boolean) || [];

          formik.setValues({
            title: response.data.title || "",
            reportType: (response.data.report_type ?? "1").toString(),
            iframeTag: response.data.iframe_tag || "",
            companies: companyIds,
          });
        } else {
          toast.error("Failed to load report");
        }
      } catch (error) {
        console.error("Error fetching report:", error);
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchReport();
    }
  }, [id, dispatch]);

  const handleCompanySelect = (companyId: string) => {
    const currentCompanies = [...(formik.values.companies || [])];
    const index = currentCompanies.indexOf(companyId);

    if (index === -1) {
      currentCompanies.push(companyId);
    } else {
      currentCompanies.splice(index, 1);
    }

    formik.setFieldValue("companies", currentCompanies, true);
  };

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-left-0 tw-right-0 tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <button
                onClick={() => navigate(-1)}
                className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer hover:tw-opacity-80"
              >
                <img
                  src={BackIcon}
                  alt="Back Icon"
                  className="tw-cursor-pointer tw-w-6 tw-h-6"
                />
              </button>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Edit Report
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-mt-[147px] tw-px-8 tw-pb-8">
        <div className="tw-p-6">
          <form onSubmit={formik.handleSubmit}>
            <div className="tw-bg-white tw-border tw-rounded-2xl tw-border-[#E9EAEB] tw-p-8">
              <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2 tw-mb-6">
                <div className="form-group">
                  <p className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                    Report Title
                  </p>
                  <TextField
                    fullWidth
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    variant="outlined"
                    placeholder="Enter Report Title"
                    className="tw-mb-4"
                  />
                </div>

                <div className="form-group">
                  <p className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                    Select Report Type
                  </p>
                  <RadioGroup
                    row
                    name="reportType"
                    value={formik.values.reportType}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="Daily"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio />}
                      label="Weekly"
                    />
                    <FormControlLabel
                      value="3"
                      control={<Radio />}
                      label="Monthly"
                    />
                  </RadioGroup>
                </div>

                <div className="form-group">
                  <p className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                    Paste Iframe Tag
                  </p>
                  <TextField
                    fullWidth
                    name="iframeTag"
                    value={formik.values.iframeTag}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.iframeTag &&
                      Boolean(formik.errors.iframeTag)
                    }
                    helperText={
                      formik.touched.iframeTag && formik.errors.iframeTag
                    }
                    variant="outlined"
                    placeholder='<iframe src="https://www.test.com"></iframe>'
                    className="tw-mb-4"
                  />
                </div>
              </div>
              <div className="tw-grid tw-gap-2">
                <div className="form-group">
                  <h6 className="tw-mb-2 tw-font-medium tw-font-semibold ">
                    Select Company
                  </h6>
                  {formik.touched.companies && formik.errors.companies && (
                    <p className="tw-text-red-100 tw-text-sm tw-mt-1">
                      {formik.errors.companies as string}
                    </p>
                  )}
                  <div className="tw-full">
                    {loading ? (
                      <div className="tw-flex tw-justify-center tw-items-center">
                        <CircularProgress />
                      </div>
                    ) : (
                      <>
                        {companies.map((tier) => (
                          <div
                            key={tier.id}
                            className="tw-rounded-lg tw-overflow-hidden tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-shadow-sm tw-my-3"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedSection(
                                  expandedSection === tier.id.toString()
                                    ? ""
                                    : tier.id.toString(),
                                )
                              }
                              className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-text-left tw-transition-colors
                                                    ${expandedSection === tier.id.toString() ? "tw-bg-[#ECF1F8] tw-text-[#232323]" : ""}`}
                            >
                              <span className="tw-font-medium">
                                {tier.user_type}
                              </span>
                              <img
                                src={ArrowDownIcon}
                                alt="Arrow Down Icon"
                                className={`tw-transition-transform ${expandedSection === tier.id.toString() ? "tw-rotate-180" : ""}`}
                              />
                            </button>

                            {expandedSection === tier.id.toString() && (
                              <div className="tw-p-4 tw-bg-white tw-border-t">
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                                  {tier.companies.map((company) => (
                                    <div
                                      key={company.id}
                                      onClick={() =>
                                        handleCompanySelect(
                                          company.id.toString(),
                                        )
                                      }
                                      className={`tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-cursor-pointer tw-border-[#E9EAEB] tw-bg-white`}
                                    >
                                      <div className="tw-flex tw-justify-between tw-gap-2">
                                        <div className="tw-flex tw-gap-2">
                                          <S3Image
                                            src=""
                                            alt="logo"
                                            className="tw-w-6 tw-h-6 tw-rounded-full"
                                          />
                                          <div>
                                            <h6
                                              className={`tw-text-base tw-mb-1 tw-font-medium`}
                                            >
                                              {company.name}
                                            </h6>
                                          </div>
                                        </div>
                                        <div className="tw-flex tw-items-start">
                                          <div
                                            className={`tw-w-5 tw-h-5 tw-rounded tw-border tw-flex tw-items-center tw-justify-center tw-bg-white
                                              ${
                                                formik.values.companies.includes(
                                                  company.id.toString(),
                                                )
                                                  ? "tw-border-[#2563EB]"
                                                  : "tw-border-[#E9EAEB]"
                                              }`}
                                          >
                                            {formik.values.companies.includes(
                                              company.id.toString(),
                                            ) && (
                                              <svg
                                                className="tw-w-3 tw-h-3"
                                                viewBox="0 0 12 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M10 3L4.5 8.5L2 6"
                                                  stroke="blue"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="tw-flex tw-justify-center tw-mt-6">
              <button
                type="submit"
                className="primary-custom-button tw-px-8 tw-py-2 tw-rounded-md tw-text-white"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditReport;
